import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages auto-scroll behavior and the visibility of the "scroll down" button.
 */
export function useAutoScroll() {
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
    const [shouldShowButton, setShouldShowButton] = useState(false);
    const showButtonTimeoutRef = useRef(null);
    const messagesContainerRef = useRef(null);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (autoScrollEnabled && messagesContainerRef.current) {
            requestAnimationFrame(() => {
                const container = messagesContainerRef.current;
                if (!container) return;

                try {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior,
                    });
                } catch (error) {
                    // no-op
                }
            });
        }
    }, [autoScrollEnabled]);

    const handleScroll = useCallback(() => {
        if (!messagesContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isNearBottom = (scrollHeight - clientHeight - scrollTop) <= 100;
        setAutoScrollEnabled(isNearBottom);
    }, []);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        if (autoScrollEnabled) {
            if (showButtonTimeoutRef.current) {
                clearTimeout(showButtonTimeoutRef.current);
                showButtonTimeoutRef.current = null;
            }
            setShouldShowButton(false);
        } else {
            if (!showButtonTimeoutRef.current) {
                showButtonTimeoutRef.current = setTimeout(() => {
                    if (!autoScrollEnabled) {
                        setShouldShowButton(true);
                    }
                    showButtonTimeoutRef.current = null;
                }, 150);
            }
        }

        return () => {
            if (showButtonTimeoutRef.current) {
                clearTimeout(showButtonTimeoutRef.current);
                showButtonTimeoutRef.current = null;
            }
        };
    }, [autoScrollEnabled]);

    return {
        autoScrollEnabled,
        setAutoScrollEnabled,
        shouldShowButton,
        messagesContainerRef,
        scrollToBottom,
    };
}
